import React, { useCallback, useMemo, useState } from 'react';
import { RowCentered, Text } from '@ydistri/ds';
import { Dropdown, DropdownProps, Tooltip } from 'antd';
import MoreIconButton from '../../../components/buttons/MoreIconButton';
import { icons, IconType } from '../../../components/icons/icons';
import AsoRequestSelect, { AsoRequestSelectProps } from '../components/AsoRequestSelect';
import CreateAsoRequestModal from './CreateAsoRequestModal';
import EditAsoRequestModal from './EditAsoRequestModal';
import DeleteAsoRequestModal from './DeleteAsoRequestModal';
import { useGetAsoRequestsQuery, useGetAsoScenarioListQuery } from '../apiAso';
import { useNavigate } from 'react-router';
import { generatePartialPath } from '../../../routes/routerSlice';
import { ROUTES } from '../../../components/menu/getMenuLeftItems';
import { useStableParams } from '../../../routes/useStableParams';
import { CreateAsoButton } from '../components/CreateAsoButton';

enum RequestAction {
  CREATE = 'CREATE',
  EDIT = 'EDIT',
  DELETE = 'DELETE',
}

const dropdownAlign: DropdownProps['align'] = { offset: [0, -6] };

interface AsoRequestProps {
  backgroundVariant?: AsoRequestSelectProps['backgroundVariant'];
}

const AsoRequest: React.FC<AsoRequestProps> = ({ backgroundVariant = 'default' }) => {
  const { projectShortName, asoRequestId } = useStableParams();

  const [openedActionModal, setOpenedActionModal] = useState<RequestAction | undefined>(undefined);
  const { data: requests } = useGetAsoRequestsQuery();
  const { data: asoScenarioList, isFetching: isAsoScenarioListFetching } =
    useGetAsoScenarioListQuery(undefined, { skip: !asoRequestId });
  const navigate = useNavigate();

  // eslint-disable-next-line @ydistri/react/no-primitive-usememo -- there may be many requests, we do not want to iterate them on every render
  const currentRequestName = useMemo(() => {
    if (!asoRequestId || !requests) return undefined;
    return requests.find(r => r.id === asoRequestId)?.name;
  }, [requests, asoRequestId]);

  const currentRequestHasScenarios =
    !!asoRequestId &&
    (asoScenarioList?.some(scenario => scenario.asoRequestId === asoRequestId) ?? false);

  // eslint-disable-next-line @ydistri/react/no-primitive-usememo -- we recalculate only when external data changes or load
  const deleteDisabledReason = useMemo(() => {
    if (currentRequestHasScenarios) {
      return 'Delete assigned ASO scenarios before deleting this request.';
    }

    if (asoRequestId && isAsoScenarioListFetching && !asoScenarioList) {
      return 'Checking assigned ASO scenarios...';
    }

    return undefined;
  }, [asoRequestId, asoScenarioList, currentRequestHasScenarios, isAsoScenarioListFetching]);

  const deleteDisabled =
    !asoRequestId || currentRequestHasScenarios || (isAsoScenarioListFetching && !asoScenarioList);

  const deleteLabel = useMemo(() => {
    const label = <span>Delete</span>;

    if (!deleteDisabledReason) {
      return label;
    }

    return (
      <Tooltip title={deleteDisabledReason} placement="left">
        {label}
      </Tooltip>
    );
  }, [deleteDisabledReason]);

  const dropdownMenu: DropdownProps['menu'] = useMemo(() => {
    return {
      items: [
        {
          label: 'Edit current ASO request',
          key: '1',
          icon: icons[IconType.EDIT_OUTLINED],
          onClick: () => setOpenedActionModal(RequestAction.EDIT),
          disabled: !asoRequestId,
        },
        {
          label: 'Create new',
          key: '2',
          icon: icons[IconType.PLUS_OUTLINED],
          onClick: () => setOpenedActionModal(RequestAction.CREATE),
        },
        { type: 'divider' as const },
        {
          label: deleteLabel,
          key: '3',
          icon: icons[IconType.DELETE_OUTLINED],
          onClick: () => setOpenedActionModal(RequestAction.DELETE),
          danger: true,
          disabled: deleteDisabled,
        },
      ],
    };
  }, [asoRequestId, deleteDisabled, deleteLabel]);

  const handleAsoRequestChange = useCallback(
    (value: number | undefined) => {
      if (!projectShortName) return;

      const params: Record<string, string | undefined> = { projectShortName };
      if (value !== undefined) {
        params.asoRequestId = String(value);
      }

      const path = generatePartialPath(ROUTES.asoChildRoute, params, undefined);
      navigate(path);
    },
    [projectShortName, navigate],
  );

  return (
    <>
      <RowCentered $gap="0.5rem">
        <Text $bold $width="8rem">
          ASO Request:{' '}
        </Text>
        {requests ? (
          <>
            <AsoRequestSelect
              onChange={value => handleAsoRequestChange(value)}
              requestId={asoRequestId}
              createButtonAction={() => setOpenedActionModal(RequestAction.CREATE)}
              backgroundVariant={backgroundVariant}
            />
            <Dropdown trigger={['click']} menu={dropdownMenu} align={dropdownAlign}>
              <div>
                <MoreIconButton />
              </div>
            </Dropdown>
          </>
        ) : (
          <CreateAsoButton
            onClick={() => setOpenedActionModal(RequestAction.CREATE)}
            title="Create ASO Request"
          />
        )}
      </RowCentered>

      {openedActionModal === RequestAction.CREATE && (
        <CreateAsoRequestModal open={true} onCancel={() => setOpenedActionModal(undefined)} />
      )}

      {openedActionModal === RequestAction.EDIT && asoRequestId && (
        <EditAsoRequestModal
          open={true}
          requestId={asoRequestId}
          currentName={currentRequestName}
          onCancel={() => setOpenedActionModal(undefined)}
        />
      )}

      {openedActionModal === RequestAction.DELETE && asoRequestId && !deleteDisabled && (
        <DeleteAsoRequestModal
          requestId={asoRequestId}
          requestName={currentRequestName}
          onCancel={() => setOpenedActionModal(undefined)}
        />
      )}
    </>
  );
};

export default AsoRequest;
